import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ReferralCard from "@/components/ReferralCard";
import MemberMessages from "@/components/MemberMessages";
import BillingCard from "@/components/BillingCard";
import DocumentUploadPanel from "@/components/DocumentUploadPanel";
import { api, getApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Circle, FileSignature, ArrowRight, Hash, Home as HomeIcon, Pencil, Trash2, Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";

const STEP_ORDER = ["intake", "docs_review", "audit", "counterclaim_filed", "trustee_pledge", "settlement", "completed"];

const FORMS = [
  { key: "nda", label: "NDA" },
  { key: "non_disclosure", label: "Trustee Non-Disclosure" },
  { key: "membership", label: "Membership" },
  { key: "moral_clause", label: "Moral Clause" },
  { key: "civil_rights", label: "Civil Rights" },
  { key: "mediation", label: "Mediation & Arbitration" },
];

export default function MemberPortal() {
  const { user } = useAuth();
  const [application, setApplication] = useState(null);
  const [steps, setSteps] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [files, setFiles] = useState([]);
  const [myListings, setMyListings] = useState([]);

  const refresh = async () => {
    try {
      const [a, s, f, c, l] = await Promise.all([
        api.get("/applications/me"),
        api.get("/esign/me"),
        api.get("/files/me"),
        api.get("/content/landing"),
        api.get("/listings/me"),
      ]);
      setApplication(a.data && a.data.application_id ? a.data : null);
      setSignatures(s.data);
      setFiles(f.data);
      setSteps(c.data.process_steps);
      setMyListings(l.data);
    } catch (e) {
      toast.error(getApiError(e));
    }
  };

  useEffect(() => { refresh(); }, []);

  const removeListing = async (listing_id) => {
    if (!window.confirm("Remove this listing?")) return;
    try {
      await api.delete(`/listings/${listing_id}`);
      toast.success("Listing removed");
      refresh();
    } catch (err) { toast.error(getApiError(err)); }
  };

  const currentIdx = application ? STEP_ORDER.indexOf(application.status) : -1;
  const progress = application ? ((currentIdx + 1) / STEP_ORDER.length) * 100 : 0;
  const isNewMember = files.length === 0 && signatures.length === 0 && !application;

  return (
    <div className="min-h-screen bg-paper" data-testid="member-portal">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 py-10 md:py-20">
        <div className="grid lg:grid-cols-12 gap-6 items-end mb-8 md:mb-12">
          <div className="lg:col-span-8">
            <div className="overline mb-3">§ Member Portal · MMXXVI</div>
            <h1 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-balance" data-testid="member-greeting">
              Welcome, {user?.name?.split(" ")[0] || "Member"}.
            </h1>
          </div>
          <div className="lg:col-span-4 flex gap-2 flex-wrap lg:justify-end">
            {!application && (
              <Link to="/apply" className="btn-primary" data-testid="member-apply-btn">
                Start application <ArrowRight className="h-4 w-4" />
              </Link>
            )}
            <Link to="/pricing" className="btn-outline" data-testid="member-pricing-btn">
              Pay fees
            </Link>
          </div>
        </div>

        {/* New-member welcome banner — points at document upload */}
        {isNewMember && (
          <div className="mb-8 md:mb-10 border border-stone-900 bg-stone-900 text-stone-50 p-6 md:p-8 flex items-start gap-5" data-testid="member-welcome-banner">
            <Sparkles className="h-6 w-6 text-stone-50 flex-shrink-0" />
            <div className="flex-1">
              <div className="overline text-stone-400 mb-2">§ Getting Started</div>
              <h2 className="font-serif font-light text-2xl md:text-3xl leading-tight">
                Send your first documents to your trustee below.
              </h2>
              <p className="text-sm text-stone-300 font-light mt-3 max-w-2xl">
                Your ledger audit begins as soon as your intake documents arrive. Upload a mortgage
                statement, deed, government ID, and any court paperwork you have. The trustee is
                notified instantly.
              </p>
            </div>
            <a
              href="#upload"
              className="hidden md:inline-flex items-center gap-2 border border-stone-50 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-stone-50 hover:text-stone-900 transition-colors"
              data-testid="member-welcome-cta"
            >
              Upload now <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
        )}

        {/* Billing */}
        <div className="mb-8 md:mb-10">
          <BillingCard />
        </div>

        {/* PROMINENT UPLOAD PANEL — always visible near top */}
        <div id="upload" className="scroll-mt-24 mb-8 md:mb-10">
          <DocumentUploadPanel files={files} onUploaded={refresh} spotlight={isNewMember} />
        </div>

        {/* Case tracker */}
        <section className="border border-stone-200 bg-white mb-8 md:mb-10" data-testid="member-case-tracker">
          <header className="px-6 md:px-8 py-6 border-b border-stone-200 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="overline">§ Case Timeline</div>
              <h2 className="font-serif font-light text-2xl md:text-3xl leading-tight mt-1">60-day case tracker</h2>
            </div>
            {application?.tracking_number && (
              <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-stone-900 border border-stone-900 px-3 py-1.5" data-testid="member-tracking-badge">
                <Hash className="h-3 w-3" /> {application.tracking_number}
              </div>
            )}
          </header>
          <div className="p-6 md:p-8">
            {!application ? (
              <p className="text-stone-600 font-light" data-testid="member-no-application">
                Submit your counterclaim application to begin the 60-day timeline.
              </p>
            ) : (
              <>
                <div className="h-1 bg-stone-200 relative overflow-hidden">
                  <div className="absolute inset-y-0 left-0 bg-stone-900 transition-all" style={{ width: `${progress}%` }} />
                </div>
                <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6" data-testid="member-process-grid">
                  {steps.map((s, i) => {
                    const done = i <= currentIdx;
                    const current = i === currentIdx;
                    return (
                      <li
                        key={s.key}
                        className={`p-4 border ${current ? "border-stone-900 bg-stone-900 text-stone-50" : done ? "border-stone-900 bg-white" : "border-stone-200 bg-white"}`}
                      >
                        <div className="flex items-center gap-2">
                          {done ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4 text-stone-400" />}
                          <span className={`caption ${current ? "text-stone-300" : "text-stone-500"}`}>{s.days}</span>
                        </div>
                        <h3 className={`font-serif font-light text-lg mt-2 ${current ? "text-stone-50" : "text-stone-900"}`}>{s.label}</h3>
                        <p className={`text-xs mt-2 leading-relaxed font-light ${current ? "text-stone-300" : "text-stone-600"}`}>{s.desc}</p>
                      </li>
                    );
                  })}
                </ol>
              </>
            )}
          </div>
        </section>

        {/* E-sign list + Messages side-by-side */}
        <div className="grid lg:grid-cols-2 gap-6 md:gap-10 mb-8 md:mb-10">
          <section className="border border-stone-200 bg-white">
            <header className="px-6 md:px-8 py-6 border-b border-stone-200 flex items-center gap-3">
              <FileSignature className="h-5 w-5" />
              <div>
                <div className="overline">§ Agreements</div>
                <h2 className="font-serif font-light text-2xl leading-tight mt-1">Required signatures</h2>
              </div>
            </header>
            <ul className="divide-y divide-stone-200">
              {FORMS.map((f) => {
                const signed = signatures.find((s) => s.form_type === f.key);
                return (
                  <li key={f.key} className="px-6 md:px-8 py-4 flex items-center justify-between gap-3" data-testid={`member-form-row-${f.key}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-serif text-lg text-stone-900 truncate">{f.label}</div>
                      {signed && <div className="caption text-stone-500 mt-1">Signed {new Date(signed.signed_at).toLocaleDateString()}</div>}
                    </div>
                    <Link
                      to={`/esign/${f.key}`}
                      className={signed ? "btn-ghost" : "btn-primary py-2 px-4 text-[11px]"}
                      data-testid={`member-sign-btn-${f.key}`}
                    >
                      {signed ? "View" : "Sign"}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>

          <MemberMessages />
        </div>

        {/* Referral + My listings */}
        <div className="grid lg:grid-cols-2 gap-6 md:gap-10">
          <ReferralCard />

          <section className="border border-stone-200 bg-white" data-testid="member-my-listings">
            <header className="px-6 md:px-8 py-6 border-b border-stone-200 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <HomeIcon className="h-5 w-5" />
                <div>
                  <div className="overline">§ Marketplace</div>
                  <h2 className="font-serif font-light text-2xl leading-tight mt-1">My listings</h2>
                </div>
              </div>
              <Link to="/listings/new" className="btn-outline text-[11px] py-2 px-4" data-testid="member-add-listing-btn">
                <Plus className="h-3.5 w-3.5" /> Add
              </Link>
            </header>
            <div className="p-6 md:p-8 space-y-3">
              {myListings.length === 0 && (
                <p className="text-sm text-stone-600 font-light">You haven&apos;t posted any listings yet.</p>
              )}
              {myListings.map((l) => (
                <div key={l.listing_id} className="border border-stone-200 p-4 flex items-center gap-3 flex-wrap" data-testid={`member-listing-${l.listing_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-serif text-lg truncate">{l.title}</span>
                      {!l.active && <span className="caption text-stone-500 border border-stone-200 px-2 py-0.5">Removed</span>}
                      {l.active && <span className="caption text-stone-700 border border-stone-300 px-2 py-0.5 capitalize">{l.listing_type.replace("_", " ")}</span>}
                    </div>
                    <div className="caption text-stone-500 mt-1 truncate">
                      {l.city}, {l.state} · {l.price ? `$${Number(l.price).toLocaleString()}` : "no price"}
                    </div>
                  </div>
                  {l.active && (
                    <div className="flex gap-2">
                      <Link to={`/listings/${l.listing_id}/edit`} className="btn-outline text-[11px] py-1.5 px-3" data-testid={`member-listing-edit-${l.listing_id}`}>
                        <Pencil className="h-3 w-3" /> Edit
                      </Link>
                      <button
                        onClick={() => removeListing(l.listing_id)}
                        className="btn-outline text-[11px] py-1.5 px-3"
                        data-testid={`member-listing-delete-${l.listing_id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
